/**
 * §1 Baseline 2025 — the four charts one survey series can be drawn as.
 *
 * These are hand-rolled SVG in the same register as `components/charts/`, and they live
 * here rather than there for one reason: the baseline's charts are the only ones on the
 * portal whose row set is a *filter* rather than a fixed four countries, and whose scale is
 * a survey share with a reference line on it. Folding four more modes into `BarsH` would
 * have made the shared primitive answer to a section instead of the other way round.
 *
 * What they share with the rest of the portal is what a reader notices: the shared
 * `ChartTip` portal (§F2 — one tooltip node on the page), the §G8 country palette, mono
 * numbers, an em dash where a survey measured nothing, and no bar at all for it. Geometry
 * is in viewBox units and the SVG is stretched to the card with `width:100%`, which is how
 * every other chart in this codebase is responsive.
 *
 * The reading rules the client asked for — *"clear not compact"* — are constants here, so
 * they cannot drift between the four: a 28-unit bar, at least 18 units of air between two
 * of them, 11–12 unit labels, and a value printed beside every bar rather than read off an
 * axis.
 */
import { useTranslation } from 'react-i18next';

import type { DashboardBaseline, DashboardBaselineSeries } from '../../lib/api';
import { hideChartTip, showChartTip } from '../../components/charts/chartTipStore';
import { EMDASH, fmtNum } from '../../lib/format';
import {
  axisValue,
  baselineRefs,
  cardTitle,
  COMPOSITION_COLORS,
  fmtValue,
  hasEstimate,
  sortRows,
  type BaselineAxisRow,
  type BaselineChartSpec,
  type BaselineView,
} from './baseline';

/* ---------- geometry (viewBox units; the SVG is scaled to the card) ---------- */

/** The plot's own width. A card at the section's two-column width lands near 1:1. */
export const BL_W = 560;
/** One bar's thickness, and the air under it. "Clear not compact" is these two numbers. */
export const BL_ROW_H = 28;
export const BL_ROW_GAP = 20;
/** The gutter the row names sit in, and the room a value label needs to the right. */
export const BL_LABEL_W = 116;
export const BL_VALUE_W = 62;
export const BL_PAD_TOP = 12;
/** The x-axis strip under a horizontal chart. */
export const BL_AXIS_H = 24;
/** A floor, so a one-row chart still reads as a chart — and a ceiling on nothing else. */
export const BL_MIN_H = 96;
/** Type sizes: the row names, and the numbers beside the bars. */
export const BL_LABEL_SIZE = 12;
export const BL_VALUE_SIZE = 11.5;
/** A share axis is always 0–100; an amount chart makes its own and adds this headroom. */
export const BL_HEADROOM = 1.12;

const plotX = BL_LABEL_W;
const plotW = BL_W - BL_LABEL_W - BL_VALUE_W;
const pitch = BL_ROW_H + BL_ROW_GAP;

/**
 * The body a given number of rows needs — it *grows*, it does not sit in a fixed frame.
 *
 * A country scope in the countries view is one row, and padding that out to the height of a
 * four-country chart left a card three-quarters empty, which reads as a chart that failed to
 * load rather than as a comparison against the programme line (screenshot review, round 1).
 */
function bodyHeight(rowCount: number): number {
  return Math.max(BL_MIN_H, BL_PAD_TOP + Math.max(rowCount, 1) * pitch + BL_AXIS_H);
}

/**
 * A row name cut to its gutter.
 *
 * SVG has no `text-overflow`, and a watershed called "Haut bassin du Sourou" drawn in full
 * runs out of the left edge of the viewBox, where it is silently clipped by the frame
 * rather than ellipsised. The full name stays on the row's own `<title>`.
 */
function clip(text: string, width = BL_LABEL_W - 10, size = BL_LABEL_SIZE): string {
  const budget = Math.max(4, Math.floor(width / (size * 0.55)));
  return text.length <= budget ? text : `${text.slice(0, budget - 1).trimEnd()}…`;
}

/**
 * The width of one bar — and **zero draws nothing**.
 *
 * The 2-unit floor exists so a 0.3 % bar is still visible rather than a hairline of nothing;
 * applying it to a measured zero would have drawn the same stub for "none of them do this"
 * as for "almost none", which is the one distinction a survey chart must not lose (review,
 * fix round 2). A `null` draws nothing either, and prints an em dash instead.
 */
function barLength(value: number | null, max: number, span: number): number {
  if (value === null || value === 0 || !Number.isFinite(value)) return 0;
  return Math.max(2, (Math.abs(value) / max) * span);
}

/**
 * Where a bar's printed figure starts — past a reference line it would otherwise sit on.
 *
 * A dashed rule a few points beyond a bar's end lands inside the label that follows it, and
 * the label's paper halo then eats a chunk of the rule. Nudging the figure past the line
 * keeps both readable and keeps the number attached to its own bar: the gap it opens is
 * always small, because the line is by definition close.
 */
function numberX(barEnd: number, text: string, refs: { value: number }[], max: number): number {
  const start = barEnd + 8;
  const end = start + text.length * BL_VALUE_SIZE * 0.62;
  for (const ref of refs) {
    const x = plotX + (max > 0 ? Math.min(ref.value / max, 1) : 0) * plotW;
    if (x >= start - 4 && x <= end + 2) return x + 8;
  }
  return start;
}

/**
 * A pair's two bars are two shades of the row's **own** colour — the same country, asked
 * twice. Two hues would have said "two categories", and the categories here are the rows.
 *
 * Module-private: an exported array costs this file a `react-refresh` warning, and no other
 * file needs it.
 */
const PAIR_SHADE = [1, 0.55];

/** Full strength for the scope's own row; 70 % for the rest, but only if there *is* a scope row. */
function rowOpacity(row: BaselineAxisRow, rows: BaselineAxisRow[]): number {
  return !rows.some((r) => r.selected) || row.selected ? 1 : 0.7;
}

export interface BaselineChartProps {
  spec: BaselineChartSpec;
  baseline: DashboardBaseline;
  rows: BaselineAxisRow[];
  view: BaselineView;
}

export interface BaselineChartCardProps extends BaselineChartProps {
  /** The sheet the card sits under — its name comes off the front of the card's title. */
  sheetLabel: string;
}

/* ---------- shared pieces ---------- */

/** The dashed programme/country markers, drawn over the bars but under nothing else. */
function RefLines({
  series,
  baseline,
  view,
  max,
  top,
  height,
}: {
  series: DashboardBaselineSeries;
  baseline: DashboardBaseline;
  view: BaselineView;
  max: number;
  top: number;
  height: number;
}) {
  const refs = baselineRefs(series, baseline, view);
  return (
    <>
      {refs.map((ref) => {
        const x = plotX + (max > 0 ? Math.min(ref.value / max, 1) : 0) * plotW;
        return (
          <line
            key={ref.kind}
            className={`bl-ref bl-ref-${ref.kind}`}
            x1={x}
            x2={x}
            y1={top}
            y2={top + height}
          />
        );
      })}
    </>
  );
}

/**
 * Three ticks — 0, half, and the top of the scale — under a share chart.
 *
 * The only axis a share chart gets (§G8: one axis per chart), and three marks rather than
 * five because every bar already prints its own figure beside it: the axis is here to say
 * *what the width means*, not to be read off.
 */
function ShareAxis({ y, max, unit, fmt }: { y: number; max: number; unit: string; fmt: number }) {
  const ticks = [0, max / 2, max];
  return (
    <g className="bl-axis" aria-hidden="true">
      <line x1={plotX} x2={plotX + plotW} y1={y} y2={y} />
      {ticks.map((tick, i) => (
        <text
          key={tick}
          x={plotX + (max > 0 ? tick / max : 0) * plotW}
          y={y + 14}
          textAnchor={i === 0 ? 'start' : i === ticks.length - 1 ? 'end' : 'middle'}
        >
          {fmtValue(tick, fmt, unit)}
        </text>
      ))}
    </g>
  );
}

/** One legend entry — a swatch (or a dashed rule) and a word. */
interface LegendItem {
  key: string;
  label: string;
  color: string;
  dashed?: boolean;
  /** Mirrors the bar's own fade, so a pair's two swatches are not one colour twice. */
  opacity?: number;
}

function Legend({ items }: { items: LegendItem[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="bl-legend">
      {items.map((item) => (
        <li key={item.key}>
          <span
            className={`bl-swatch${item.dashed ? ' dashed' : ''}`}
            style={
              item.dashed
                ? { borderColor: item.color }
                : { background: item.color, opacity: item.opacity ?? 1 }
            }
            aria-hidden="true"
          />
          {item.label}
        </li>
      ))}
    </ul>
  );
}

/** The reference-line legend entries, which every comparison chart carries the same way. */
function useRefLegend(series: DashboardBaselineSeries, baseline: DashboardBaseline, view: BaselineView) {
  const { t } = useTranslation();
  return baselineRefs(series, baseline, view).map((ref) => ({
    key: `ref-${ref.kind}`,
    label: `${t(`dashboard.baseline.chart.${ref.kind}`)} · ${fmtValue(ref.value, series.fmt, series.unit)}`,
    color: ref.kind === 'programme' ? '#55614F' : '#C77B21',
    dashed: true,
  }));
}

/** Hover handlers for one datum, through the one tooltip node the page has. */
function tipProps(label: string, value: string) {
  return {
    onMouseMove: (e: { clientX: number; clientY: number }) => showChartTip(e, label, value),
    onMouseLeave: hideChartTip,
  };
}

/* ---------- 1. one share, one bar per row ---------- */

function BarsChart({ spec, baseline, rows, view }: BaselineChartProps) {
  const series = spec.members[0]!.series;
  const ordered = sortRows(rows, series, view);
  const refs = baselineRefs(series, baseline, view);
  const values = ordered.map((r) => axisValue(series, r, view).value ?? 0);
  // A share is read against 100; an axis fitted to the bars would make 12 % look like 80 %.
  const max = Math.max(100, ...values, ...refs.map((r) => r.value));
  const h = bodyHeight(ordered.length);
  const axisY = h - BL_AXIS_H;

  return (
    <svg className="bl-chart" viewBox={`0 0 ${BL_W} ${h}`} role="img" aria-label={readout(spec, ordered, view)}>
      <ShareAxis y={axisY} max={max} unit={spec.unit} fmt={spec.fmt} />
      {/* Under the bars, not over them: a dashed rule drawn last crossed every printed
          figure on the chart (screenshot review, round 1). */}
      <RefLines series={series} baseline={baseline} view={view} max={max} top={BL_PAD_TOP - 6} height={axisY - BL_PAD_TOP + 6} />
      {ordered.map((row, i) => {
        const { value, n } = axisValue(series, row, view);
        const y = BL_PAD_TOP + i * pitch;
        const w = barLength(value, max, plotW);
        const text = fmtValue(value, spec.fmt, spec.unit);
        return (
          <g key={row.key} className="bl-row" {...tipProps(`${row.label}${n === null ? '' : ` · n=${fmtNum(n, 0)}`}`, text)}>
            <title>{`${row.label} · ${text}`}</title>
            <text className="bl-rowlabel" x={BL_LABEL_W - 10} y={y + BL_ROW_H / 2 + 4} textAnchor="end">
              {clip(row.label)}
            </text>
            {w === 0 ? null : (
              <rect x={plotX} y={y} width={w} height={BL_ROW_H} rx={3} fill={row.color} opacity={rowOpacity(row, ordered)} />
            )}
            <text className="bl-num" x={numberX(plotX + w, text, refs, max)} y={y + BL_ROW_H / 2 + 4}>
              {text}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* ---------- 2. a two-answer block: two bars per row ---------- */

function GroupedChart({ spec, baseline, rows, view }: BaselineChartProps) {
  const [first, second] = spec.members;
  const ordered = sortRows(rows, first!.series, view);
  const refs = baselineRefs(first!.series, baseline, view);
  const all = spec.members.flatMap((m) => ordered.map((r) => axisValue(m.series, r, view).value ?? 0));
  const max = Math.max(100, ...all, ...refs.map((r) => r.value));
  const h = bodyHeight(ordered.length);
  const axisY = h - BL_AXIS_H;
  const barH = (BL_ROW_H - 4) / 2;

  return (
    <svg className="bl-chart" viewBox={`0 0 ${BL_W} ${h}`} role="img" aria-label={readout(spec, ordered, view)}>
      <ShareAxis y={axisY} max={max} unit={spec.unit} fmt={spec.fmt} />
      <RefLines series={first!.series} baseline={baseline} view={view} max={max} top={BL_PAD_TOP - 6} height={axisY - BL_PAD_TOP + 6} />
      {ordered.map((row, i) => {
        const y = BL_PAD_TOP + i * pitch;
        return (
          <g key={row.key} className="bl-row">
            <text className="bl-rowlabel" x={BL_LABEL_W - 10} y={y + BL_ROW_H / 2 + 4} textAnchor="end">
              {clip(row.label)}
            </text>
            {[first!, second!].map((member, mi) => {
              const { value, n } = axisValue(member.series, row, view);
              const w = barLength(value, max, plotW);
              const by = y + mi * (barH + 4);
              const text = fmtValue(value, spec.fmt, spec.unit);
              return (
                <g
                  key={member.series.id}
                  {...tipProps(`${row.label} · ${member.label}${n === null ? '' : ` · n=${fmtNum(n, 0)}`}`, text)}
                >
                  <title>{`${row.label} · ${member.label} · ${text}`}</title>
                  {w === 0 ? null : (
                    <rect
                      x={plotX}
                      y={by}
                      width={w}
                      height={barH}
                      rx={2}
                      fill={row.color}
                      opacity={rowOpacity(row, ordered) * PAIR_SHADE[mi]!}
                    />
                  )}
                  <text className="bl-num sm" x={numberX(plotX + w, text, refs, max)} y={by + barH / 2 + 3.5}>
                    {text}
                  </text>
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
}

/* ---------- 3. a block that is a whole: one 100 % stack per row ---------- */

function StackedChart({ spec, rows, view }: BaselineChartProps) {
  // The axis order, not a ranking: a stack has no single number to rank rows by.
  const h = bodyHeight(rows.length);

  return (
    <svg className="bl-chart" viewBox={`0 0 ${BL_W} ${h}`} role="img" aria-label={readout(spec, rows, view)}>
      {rows.map((row, i) => {
        const parts = spec.members.map((m) => ({
          member: m,
          value: axisValue(m.series, row, view).value,
        }));
        const total = parts.reduce((sum, p) => sum + (p.value ?? 0), 0);
        const y = BL_PAD_TOP + i * pitch;
        let x = plotX;
        return (
          <g key={row.key} className="bl-row">
            <text className="bl-rowlabel" x={BL_LABEL_W - 10} y={y + BL_ROW_H / 2 + 4} textAnchor="end">
              {clip(row.label)}
            </text>
            {total <= 0 ? (
              <text className="bl-num" x={plotX} y={y + BL_ROW_H / 2 + 4}>
                {EMDASH}
              </text>
            ) : (
              parts.map((part, pi) => {
                const share = (part.value ?? 0) / total;
                const w = share * (plotW + BL_VALUE_W - 12);
                const sx = x;
                x += w;
                if (w <= 0) return null;
                const text = fmtValue(part.value, spec.fmt, spec.unit);
                return (
                  <g
                    key={part.member.series.id}
                    {...tipProps(`${row.label} · ${part.member.label}`, text)}
                  >
                    <title>{`${row.label} · ${part.member.label} · ${text}`}</title>
                    <rect
                      x={sx}
                      y={y}
                      width={Math.max(w - 1, 0.5)}
                      height={BL_ROW_H}
                      fill={COMPOSITION_COLORS[pi % COMPOSITION_COLORS.length]}
                      opacity={rowOpacity(row, rows)}
                    />
                    {w > 40 ? (
                      <text className="bl-seg" x={sx + w / 2} y={y + BL_ROW_H / 2 + 4} textAnchor="middle">
                        {fmtNum(share * 100, 0)}%
                      </text>
                    ) : null}
                  </g>
                );
              })
            )}
          </g>
        );
      })}
    </svg>
  );
}

/* ---------- 4. an amount: vertical bars with their own scale ---------- */

function AmountChart({ spec, baseline, rows, view }: BaselineChartProps) {
  const series = spec.members[0]!.series;
  const refs = baselineRefs(series, baseline, view);
  const values = rows.map((r) => axisValue(series, r, view).value);
  const max = Math.max(
    ...values.map((v) => (v === null ? 0 : Math.abs(v))),
    ...refs.map((r) => r.value),
    0,
  ) * BL_HEADROOM || 1;
  const h = 230;
  const padLeft = 58;
  const padBottom = 30;
  const padTop = 22;
  const plotH = h - padTop - padBottom;
  const slot = (BL_W - padLeft - 18) / Math.max(rows.length, 1);
  const barW = Math.min(64, slot * 0.58);
  const ticks = [0, max / 2, max];

  return (
    <svg className="bl-chart" viewBox={`0 0 ${BL_W} ${h}`} role="img" aria-label={readout(spec, rows, view)}>
      <g className="bl-axis" aria-hidden="true">
        {ticks.map((tick) => {
          const y = padTop + plotH - (tick / max) * plotH;
          return (
            <g key={tick}>
              <line x1={padLeft} x2={BL_W - 12} y1={y} y2={y} />
              <text x={padLeft - 8} y={y + 4} textAnchor="end">
                {fmtNum(tick, spec.fmt)}
              </text>
            </g>
          );
        })}
        <text className="bl-unit" transform={`translate(14 ${padTop + plotH / 2}) rotate(-90)`} textAnchor="middle">
          {spec.unit}
        </text>
      </g>
      {rows.map((row, i) => {
        const value = values[i] ?? null;
        const barH = barLength(value, max, plotH);
        const x = padLeft + i * slot + (slot - barW) / 2;
        const y = padTop + plotH - barH;
        const text = fmtValue(value, spec.fmt, spec.unit);
        const n = axisValue(series, row, view).n;
        return (
          <g key={row.key} className="bl-row" {...tipProps(`${row.label}${n === null ? '' : ` · n=${fmtNum(n, 0)}`}`, text)}>
            <title>{`${row.label} · ${text}`}</title>
            {barH === 0 ? null : (
              <rect x={x} y={y} width={barW} height={barH} rx={3} fill={row.color} opacity={rowOpacity(row, rows)} />
            )}
            <text className="bl-num" x={x + barW / 2} y={y - 7} textAnchor="middle">
              {text}
            </text>
            <text className="bl-rowlabel" x={x + barW / 2} y={padTop + plotH + 18} textAnchor="middle">
              {clip(row.label, slot, BL_LABEL_SIZE)}
            </text>
          </g>
        );
      })}
      <RefLinesV series={series} baseline={baseline} view={view} max={max} left={padLeft} width={BL_W - padLeft - 12} top={padTop} plotH={plotH} />
    </svg>
  );
}

/** The amount chart's reference lines, which run across rather than down. */
function RefLinesV({
  series,
  baseline,
  view,
  max,
  left,
  width,
  top,
  plotH,
}: {
  series: DashboardBaselineSeries;
  baseline: DashboardBaseline;
  view: BaselineView;
  max: number;
  left: number;
  width: number;
  top: number;
  plotH: number;
}) {
  return (
    <>
      {baselineRefs(series, baseline, view).map((ref) => {
        const y = top + plotH - (Math.min(ref.value / max, 1)) * plotH;
        return (
          <line key={ref.kind} className={`bl-ref bl-ref-${ref.kind}`} x1={left} x2={left + width} y1={y} y2={y} />
        );
      })}
    </>
  );
}

/**
 * The chart as one sentence, for a reader who cannot see it.
 *
 * Every figure a sighted reader gets off the bars is in here, because on this section the
 * chart is the *only* place the per-country and per-watershed numbers appear — the compact
 * rows that used to print them are gone.
 */
function readout(spec: BaselineChartSpec, rows: BaselineAxisRow[], view: BaselineView): string {
  const body = rows
    .map((row) =>
      spec.members
        .map((m) => {
          const value = fmtValue(axisValue(m.series, row, view).value, spec.fmt, spec.unit);
          return spec.members.length > 1 ? `${m.label} ${value}` : value;
        })
        .join(', '),
    )
    .map((text, i) => `${rows[i]!.label} ${text}`)
    .join('; ');
  return `${spec.title}: ${body}`;
}

/**
 * One card: a title, what the numbers are, a legend, and the chart.
 *
 * The card is the unit the client asked for — *"each sub-indicator with its own full
 * chart"* — so everything needed to read the chart is inside it and nothing is shared with
 * the card beside it except the axis the filter set.
 */
export function BaselineChartCard({
  spec,
  baseline,
  rows,
  view,
  sheetLabel,
}: BaselineChartCardProps) {
  const { t } = useTranslation();
  const series = spec.members[0]!.series;
  const refLegend = useRefLegend(series, baseline, view);
  const empty = rows.length === 0 || !hasEstimate(spec.members, rows, view);

  const legend =
    spec.kind === 'grouped' || spec.kind === 'stacked'
      ? spec.members.map((m, i) => ({
          key: m.series.id,
          label: m.label,
          color:
            spec.kind === 'stacked'
              ? COMPOSITION_COLORS[i % COMPOSITION_COLORS.length]!
              : (rows.find((r) => r.selected) ?? rows[0])?.color ?? COMPOSITION_COLORS[0]!,
          // A pair's swatches carry the same two shades its bars do (review, fix round 2).
          opacity: spec.kind === 'grouped' ? PAIR_SHADE[i] ?? 1 : 1,
        }))
      : [];
  // A composition is normalised per row, so a programme line across it would be meaningless.
  const items = spec.kind === 'stacked' ? legend : [...legend, ...refLegend];

  return (
    <div className="card bl-card" data-testid={`baseline-chart-${spec.id}`}>
      <div className="bl-card-head">
        {/* `h5` under the sheet's own `h4`, under the section's `h3`: the card is a level
            below the subsection it sits in, and a screen reader's outline says so. */}
        <h5 className="bl-card-title" title={spec.title}>
          {cardTitle(spec.title, sheetLabel)}
        </h5>
      </div>
      <Legend items={items} />
      {empty ? (
        <p className="bl-empty" data-testid={`baseline-empty-${spec.id}`}>
          {t('dashboard.baseline.chart.none')}
        </p>
      ) : spec.kind === 'stacked' ? (
        <StackedChart spec={spec} baseline={baseline} rows={rows} view={view} />
      ) : spec.kind === 'grouped' ? (
        <GroupedChart spec={spec} baseline={baseline} rows={rows} view={view} />
      ) : spec.kind === 'amount' ? (
        <AmountChart spec={spec} baseline={baseline} rows={rows} view={view} />
      ) : (
        <BarsChart spec={spec} baseline={baseline} rows={rows} view={view} />
      )}
    </div>
  );
}

export default BaselineChartCard;
